import type { ChildAppConfig } from '@shared/app-config'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import type { ProxyApiInstance } from '@/proxy/shared/types'
import { AUTH_HEADER } from '@shared/api/api'
import { getEnv } from '@shared/config/env'
import axios from 'axios'

// Augment axios's request config so consumers can opt out of the proxy api's
// default auth handling on a per-request basis. See `skipDefaultAuth` below.
declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Skip the proxy api's default auth handling for THIS request.
     *
     * When `true`:
     *   - the request interceptor will NOT attach `Authorization: Bearer <token>`
     *   - the response interceptor will NOT call `host.handleError('auth-expired')` on 401
     *
     * Use for:
     *   - calls to a backend that uses a different auth domain
     *   - calls that legitimately probe an unauthenticated endpoint
     *   - calls where a 401 is meaningful but should not log the user out
     *
     * Cross-origin requests (URL has a different origin from the api's
     * `baseURL`) auto-skip Wippy's default auth regardless of this flag,
     * so setting it on a cross-origin call is redundant but harmless.
     */
    skipDefaultAuth?: boolean
  }
  interface InternalAxiosRequestConfig {
    skipDefaultAuth?: boolean
  }
}

type HostApi = ProxyApiInstance['host']

/**
 * Build the axios instance the proxy hands to child apps. Two interceptors:
 *
 *   1. Request: attach `Authorization: Bearer <appConfig.auth.token>` to
 *      same-origin requests by default. Skip when the request opts out via
 *      `skipDefaultAuth: true` or when the URL targets a different origin.
 *
 *   2. Response (only when `hostApi` is provided): on a `401`, fire
 *      `host.handleError('auth-expired', { url, method, message })` once
 *      per instance lifetime — single-flight so a flood of in-flight
 *      requests after a session expires doesn't bounce 30 times. Subsequent
 *      401s still reject (call-site catches still see them) but no longer
 *      re-emit `auth-expired`. Skipped under the same opt-outs as the
 *      request interceptor.
 *
 * `hostApi` is optional so unit tests / non-iframe callers can build a bare
 * instance without wiring host plumbing. In that mode the response
 * interceptor is not registered.
 */
export function createApi(
  appConfig: ChildAppConfig,
  hostApi?: HostApi,
): AxiosInstance {
  const api = axios.create({
    baseURL: getEnv().APP_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    ...appConfig.axiosDefaults,
  })

  api.interceptors.request.use((config) => {
    if (config.skipDefaultAuth)
      return config
    if (isCrossOrigin(config, api.defaults.baseURL))
      return config
    if (appConfig.auth.token)
      config.headers[AUTH_HEADER] = `Bearer ${appConfig.auth.token}`
    return config
  })

  if (hostApi) {
    let authExpiredFired = false
    api.interceptors.response.use(
      response => response,
      (error: { config?: InternalAxiosRequestConfig, response?: { status?: number }, message?: string }) => {
        const cfg = error?.config
        if (cfg?.skipDefaultAuth)
          return Promise.reject(error)
        if (isCrossOrigin(cfg, api.defaults.baseURL))
          return Promise.reject(error)
        if (error?.response?.status === 401 && !authExpiredFired) {
          authExpiredFired = true
          hostApi.handleError('auth-expired', {
            url: cfg?.url,
            method: cfg?.method,
            message: error?.message,
          })
        }
        return Promise.reject(error)
      },
    )
  }

  return api
}

/**
 * Returns true when the request URL targets a different origin than the
 * api's effective baseURL. Defense in depth so a fully-qualified URL like
 * `https://api.openai.com/v1/...` doesn't leak the Wippy token to a third
 * party (and a third-party 401 doesn't trigger our auth-expired bounce).
 *
 * Path-relative URLs (`/foo`, `foo/bar`) always resolve against baseURL —
 * same-origin by definition.
 */
function isCrossOrigin(
  config: { url?: string, baseURL?: string } | undefined,
  apiBaseURL: string | undefined,
): boolean {
  const url = config?.url || ''
  if (!/^https?:\/\//i.test(url))
    return false
  try {
    const target = new URL(url)
    const baseURL = config?.baseURL || apiBaseURL
    if (!baseURL)
      return false
    const base = new URL(
      /^https?:\/\//i.test(baseURL)
        ? baseURL
        : (typeof window !== 'undefined' && window.location?.origin
            ? window.location.origin + baseURL
            : baseURL),
    )
    return target.origin !== base.origin
  }
  catch {
    return false
  }
}
